/**
 * People — users are ordinary records with a role, managed in Settings (ADR-001 §1.4, §7.1, §11.3).
 * Seed people exist only in db/seed/ with is_seed = true.
 */
import { z } from 'zod';
import { IsoDate, PageQuery, Timestamp, Uuid, Version, page } from './common.js';
import { GrantableCapability, Role } from './roles.js';
export const UserStatus = z.enum(['active', 'disabled']).meta({ id: 'UserStatus' });
const Name = z.string().trim().min(1, 'Enter a name').max(80, 'Keep the name under 80 characters');
const Initials = z
    .string()
    .trim()
    .regex(/^[A-Za-z]{1,3}$/, 'Use 1 to 3 letters for initials, for example DK')
    .transform((s) => s.toUpperCase());
/** Phone for wa.me links: digits with optional leading +, 10–15 digits. */
const Phone = z
    .string()
    .trim()
    .regex(/^\+?[0-9]{10,15}$/, 'Enter the phone number with country code, digits only, for example +919820012345');
/** Load adjustment on top of the standard capacity (ADR §8.9, open question 9). Default 0. */
const CapacityAdjustmentPct = z.number().int().min(-100).max(100);
/**
 * The compact person reference used everywhere (§11.2).
 * `phone` is present only when the caller may send this person a WhatsApp message.
 */
export const UserRef = z
    .object({
    id: Uuid,
    name: z.string(),
    initials: z.string(),
    role: Role,
    phone: z.string().optional(),
})
    .meta({ id: 'UserRef' });
/** Full user record, as Settings → People shows it. Managers only. */
/**
 * Whether this person can sign in with a password: `none` (Google only), `set`, or `temporary` (a manager's reset or
 * the founder's bootstrap — they must change it at their next sign-in). The password itself is never returned.
 */
export const SignInPassword = z.enum(['none', 'set', 'temporary']).meta({ id: 'SignInPassword' });
export const User = UserRef.extend({
    email: z.email(),
    status: UserStatus,
    joined_on: IsoDate.nullable(),
    capacity_adjustment_pct: z.number().int(),
    /** Number of clients this user is the current SPOC for. */
    client_count: z.number().int().nonnegative(),
    /** True only for rows created by db/seed/ — sample data, removable with one DELETE. */
    is_seed: z.boolean(),
    /**
     * Capabilities granted to this person on top of their role (operator decisions 11–12), e.g. `holidays.manage`
     * (ADR-005's hr.admin). Their role's own capabilities are not listed here.
     */
    granted_capabilities: z.array(GrantableCapability),
    sign_in_password: SignInPassword,
    version: Version,
    created_at: Timestamp,
    updated_at: Timestamp,
}).meta({ id: 'User' });
// ---- GET /users -----------------------------------------------------------
export const ListUsersQuery = PageQuery.extend({
    role: Role.optional(),
    status: UserStatus.optional(),
    /** Matches name, initials or email, case-insensitive. */
    q: z.string().trim().min(1).max(80).optional(),
});
export const UserPage = page(User).meta({ id: 'UserPage' });
// ---- POST /users ----------------------------------------------------------
export const CreateUserBody = z
    .object({
    /** Must be a Google Workspace address on the allowed sign-in domain (amplispot.com). Stored lower-case. */
    email: z
        .email('Enter a full email address, for example name@amplispot.com')
        .transform((s) => s.trim().toLowerCase()),
    name: Name,
    initials: Initials,
    role: Role,
    phone: Phone.optional(),
    capacity_adjustment_pct: CapacityAdjustmentPct.optional(),
    joined_on: IsoDate.optional(),
})
    .strict();
/**
 * `201` — the new user, plus the sign-in URL to share by hand (v1 sends no email).
 * `409 conflict` when the email already belongs to a user.
 */
export const CreateUserResponse = User.extend({
    sign_in_url: z.url(),
}).meta({ id: 'CreateUserResponse' });
// ---- PATCH /users/{id} ----------------------------------------------------
export const UpdateUserBody = z
    .object({
    name: Name.optional(),
    initials: Initials.optional(),
    role: Role.optional(),
    /** `null` clears the phone. */
    phone: Phone.nullable().optional(),
    /** `disabled` deletes every session of that user in the same transaction — access ends immediately. */
    status: UserStatus.optional(),
    capacity_adjustment_pct: CapacityAdjustmentPct.optional(),
    joined_on: IsoDate.nullable().optional(),
})
    .strict()
    .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one field' });
// ---- POST /users/{id}/capture-token/rotate --------------------------------
/** The personal capture link. Shown once; rotating invalidates the previous link. */
export const CaptureTokenResponse = z
    .object({
    capture_url: z.url(),
})
    .meta({ id: 'CaptureTokenResponse' });
export const UserIdParams = z.object({ id: Uuid });
// ---- POST /users/{id}/password-reset --------------------------------------
/** The new temporary password is in this response and nowhere else: show it once, it is never readable again. */
export const PasswordResetResponse = z
    .object({
    temporary_password: z.string().min(10),
    user: User,
})
    .meta({ id: 'PasswordResetResponse' });
// ---- PUT / DELETE /users/{id}/capabilities/{capability} -------------------
export const UserCapabilityParams = z.object({ id: Uuid, capability: GrantableCapability });
//# sourceMappingURL=users.js.map